// ============================================================
// 生成 Schema（阶段一 T1）：3D 场景（dtwince.scene.schema.json）
// 落库映射：scenes.config + models（apps/dtwin-server/src/modules/{scenes,models}/entities/*）
// 字段来源：apps/dtwin-web/src/hooks/useSceneSave.ts
// ============================================================
import { z } from 'zod';
import { GENERATION_SCHEMA_VERSION, GEN_LIMITS } from './constants';
import { bundleMetaSchema, vec3Schema } from './common';

/** 场景模型（models 表；url 必须命中基座/平台资源白名单，由跨引用校验器检查） */
export const sceneModelSchema = z
  .object({
    name: z.string().min(1).max(200),
    url: z.string().min(1).max(500),
    format: z.string().min(1).max(10),
    position: vec3Schema.default([0, 0, 0]),
    rotation: vec3Schema.default([0, 0, 0]),
    scale: vec3Schema.default([1, 1, 1]),
    visible: z.boolean().default(true),
    locked: z.boolean().default(false),
    presetId: z.string().max(64).nullish(),
    materials: z.record(z.unknown()).nullish(),
  })
  .strict();
export type SceneModelSpec = z.infer<typeof sceneModelSchema>;

/** 场景 config（与 useSceneSave 保存的键一致；未列出的扩展键透传） */
export const sceneConfigSchema = z
  .object({
    environment: z.unknown().optional(),
    effects: z.unknown().optional(),
    lights: z.array(z.unknown()).optional(),
    boundedMode: z.boolean().optional(),
    showGrid: z.boolean().optional(),
    showGround: z.boolean().optional(),
    markers: z.array(z.unknown()).optional(),
    flyLines: z.array(z.unknown()).optional(),
    waypoints: z.array(z.unknown()).optional(),
    browseRoute: z.unknown().optional(),
    carousel: z.unknown().optional(),
    clipping: z.unknown().optional(),
    measurements: z.array(z.unknown()).optional(),
    layers: z.array(z.unknown()).optional(),
    cameraStrategy: z.string().max(40).optional(),
    savedCamera: z.unknown().optional(),
    pathCameras: z.array(z.unknown()).optional(),
    subObjectConfigs: z.record(z.array(z.unknown())).optional(),
    datasources: z.array(z.unknown()).optional(),
    bindings: z.array(z.unknown()).optional(),
    gis: z
      .object({
        gisState: z.unknown().optional(),
        geoFences: z.array(z.unknown()).optional(),
        heatmaps: z.array(z.unknown()).optional(),
      })
      .optional(),
  })
  .passthrough();

export const sceneBundleSchema = z
  .object({
    schemaVersion: z.literal(GENERATION_SCHEMA_VERSION),
    kind: z.literal('scene'),
    meta: bundleMetaSchema,
    config: sceneConfigSchema.default({}),
    models: z.array(sceneModelSchema).max(GEN_LIMITS.models).default([]),
  })
  .strict();

export type SceneBundle = z.infer<typeof sceneBundleSchema>;
export type SceneBundleConfig = z.infer<typeof sceneConfigSchema>;
